import React from "react";
import {Box, IconButton, Paper, Slide, Snackbar, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const TransitionComponent = (props) => {
  return <Slide {...props} direction="up" />;
};

const CustomisedSnackbar = ({
  message = "Notification",
  title,
  severity = "success",
  actionType,
  open,
  handleClose,
  anchorOrigin = {vertical: "bottom", horizontal: "center"},
  autoHideDuration = 2600,
  sx = {},
}) => {
  // Determine accent color, icon, and default title based on actionType & severity
  let accentColor = "#4ade80"; // Emerald green
  let iconBg = "rgba(34, 197, 94, 0.16)";
  let iconBorder = "rgba(34, 197, 94, 0.3)";
  let defaultTitle = "Cart Updated";
  let icon = <CheckCircleRoundedIcon sx={{fontSize: 20, color: accentColor}} />;

  if (actionType === "increase") {
    accentColor = "#4ade80";
    iconBg = "rgba(34, 197, 94, 0.18)";
    iconBorder = "rgba(34, 197, 94, 0.35)";
    defaultTitle = "Quantity Increased";
    icon = <AddCircleOutlineRoundedIcon sx={{fontSize: 20, color: accentColor}} />;
  } else if (actionType === "decrease") {
    accentColor = "#fbbf24"; // Warm amber
    iconBg = "rgba(245, 158, 11, 0.18)";
    iconBorder = "rgba(245, 158, 11, 0.35)";
    defaultTitle = "Quantity Decreased";
    icon = <RemoveCircleOutlineRoundedIcon sx={{fontSize: 20, color: accentColor}} />;
  } else if (actionType === "remove" || severity === "error") {
    accentColor = "#f87171"; // Soft rose red
    iconBg = "rgba(239, 68, 68, 0.18)";
    iconBorder = "rgba(239, 68, 68, 0.35)";
    defaultTitle = "Item Removed";
    icon = <DeleteOutlineRoundedIcon sx={{fontSize: 20, color: accentColor}} />;
  } else if (severity === "warning") {
    accentColor = "#fbbf24";
    iconBg = "rgba(245, 158, 11, 0.18)";
    iconBorder = "rgba(245, 158, 11, 0.35)";
    defaultTitle = "Warning";
    icon = <WarningAmberRoundedIcon sx={{fontSize: 20, color: accentColor}} />;
  } else if (severity === "info") {
    accentColor = "#38bdf8"; // Sky blue
    iconBg = "rgba(56, 189, 248, 0.18)";
    iconBorder = "rgba(56, 189, 248, 0.35)";
    defaultTitle = "Notice";
    icon = <InfoOutlinedIcon sx={{fontSize: 20, color: accentColor}} />;
  }

  const effectiveTitle = title || defaultTitle;

  return (
    <Snackbar
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
      open={open}
      onClose={handleClose}
      TransitionComponent={TransitionComponent}
      sx={{
        zIndex: 99999,
        // Responsive vertical positioning avoiding bottom navigation / sticky bars
        bottom: {
          xs: anchorOrigin.vertical === "bottom" ? "125px !important" : undefined,
          md: anchorOrigin.vertical === "bottom" ? "32px !important" : undefined,
        },
        top: {
          xs: anchorOrigin.vertical === "top" ? "75px !important" : undefined,
          md: anchorOrigin.vertical === "top" ? "32px !important" : undefined,
        },
        ...sx,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: "10px 14px 10px 12px",
          minWidth: {xs: 290, sm: 340},
          maxWidth: {xs: "92vw", sm: 440},
          background: "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.96) 100%)",
          color: "#f8fafc",
          borderRadius: "14px",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          boxShadow:
            "0 16px 36px -4px rgba(0, 0, 0, 0.45), 0 4px 12px -2px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(12px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle accent line on the left */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: "15%",
            bottom: "15%",
            width: 3.5,
            borderRadius: "0 4px 4px 0",
            bgcolor: accentColor,
            boxShadow: `0 0 8px ${accentColor}`,
          }}
        />

        {/* Icon Badge */}
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: iconBg,
            border: `1px solid ${iconBorder}`,
            flexShrink: 0,
            ml: 0.5,
          }}
        >
          {icon}
        </Box>

        {/* Text Content */}
        <Box sx={{flexGrow: 1, minWidth: 0, pr: 0.5}}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: accentColor,
              display: "block",
              lineHeight: 1.2,
              mb: "2px",
            }}
          >
            {effectiveTitle}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.86rem",
              color: "#f1f5f9",
              lineHeight: 1.35,
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {message}
          </Typography>
        </Box>

        {/* Dismiss Button */}
        {handleClose && (
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close notification"
            sx={{
              color: "#94a3b8",
              p: 0.5,
              borderRadius: "8px",
              transition: "all 0.15s ease",
              flexShrink: 0,
              "&:hover": {
                color: "#ffffff",
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon sx={{fontSize: 16}} />
          </IconButton>
        )}
      </Paper>
    </Snackbar>
  );
};

export default CustomisedSnackbar;
